import { Card, Tag, ProgressBar, Title, Text } from '@nimbus-ds/components'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
// Nimbus icon substitutions: MousePointer→StepsIcon (no cursor/click icon), Eye→EyeIcon,
// ShoppingCart→ShoppingCartIcon, CreditCard→CreditCardIcon, TrendingUp→ArrowupIcon,
// TrendingDown→ArrowDownIcon, CheckCircle2→CheckCircleIcon.
import { StepsIcon, EyeIcon, ShoppingCartIcon, CreditCardIcon, ArrowupIcon, ArrowDownIcon, CheckCircleIcon } from '@nimbus-ds/icons'

interface EventMetrics {
  event_name: string
  event_type?: string
  control: {
    count: number
    unique_sessions: number
    rate?: number
  }
  treatment: {
    count: number
    unique_sessions: number
    rate?: number
  }
  is_significant?: boolean
  p_value?: number
  lift_percentage?: number
  confidence_level?: number
}

interface CustomEventsAnalyticsProps {
  events: EventMetrics[]
  totalSessions: {
    control: number
    treatment: number
  }
}

const eventIcons: Record<string, any> = {
  click: StepsIcon,
  pageview: EyeIcon,
  add_to_cart: ShoppingCartIcon,
  purchase: CreditCardIcon,
}

const eventColors: Record<string, string> = {
  click: '#3b82f6',
  pageview: '#8b5cf6',
  add_to_cart: '#f59e0b',
  purchase: '#10b981',
}

export function CustomEventsAnalytics({ events, totalSessions }: CustomEventsAnalyticsProps) {
  if (!events || events.length === 0) {
    return (
      <Card>
        <Card.Header>
          <Title as="h3">Análisis de Eventos Personalizados</Title>
          <Text>
            Eventos personalizados rastreados durante el experimento
          </Text>
        </Card.Header>
        <Card.Body>
          <div className="text-center py-12">
            <StepsIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <Text>No hay eventos personalizados rastreados aún</Text>
            <Text fontSize="caption">
              Los eventos capturados desde el Editor Visual aparecerán aquí
            </Text>
          </div>
        </Card.Body>
      </Card>
    )
  }

  // Prepare data for chart
  const chartData = events.map(event => ({
    name: event.event_name,
    control: event.control.count,
    treatment: event.treatment.count,
    controlRate: event.control.rate || (event.control.unique_sessions / totalSessions.control * 100),
    treatmentRate: event.treatment.rate || (event.treatment.unique_sessions / totalSessions.treatment * 100),
  }))

  const calculateLift = (treatment: number, control: number) => {
    if (control === 0) return 0
    return ((treatment - control) / control) * 100
  }

  return (
    <div className="space-y-6">
      {/* Chart visualization */}
      <Card>
        <Card.Header>
          <Title as="h3">Comparación de Eventos entre Variantes</Title>
          <Text>
            Frecuencia de eventos personalizados en control vs tratamiento
          </Text>
        </Card.Header>
        <Card.Body>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={100}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px',
                }}
              />
              <Legend />
              <Bar dataKey="control" fill="#3b82f6" name="Control" />
              <Bar dataKey="treatment" fill="#10b981" name="Tratamiento" />
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>

      {/* Detailed event cards */}
      <Card>
        <Card.Header>
          <Title as="h3">Métricas Detalladas por Evento</Title>
          <Text>
            Análisis individual de cada evento personalizado
          </Text>
        </Card.Header>
        <Card.Body>
          <div className="space-y-4">
            {events.map((event, index) => {
              const Icon = eventIcons[event.event_type || 'click'] || StepsIcon
              const color = eventColors[event.event_type || 'click'] || '#3b82f6'

              const controlRate = event.control.rate || (event.control.unique_sessions / totalSessions.control * 100)
              const treatmentRate = event.treatment.rate || (event.treatment.unique_sessions / totalSessions.treatment * 100)
              const rateLift = calculateLift(treatmentRate, controlRate)

              const isPositiveLift = rateLift > 0

              return (
                <div
                  key={index}
                  className="p-4 rounded-lg border bg-card hover:shadow-md transition-shadow"
                >
                  {/* Event header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: `${color}20` }}
                      >
                        <Icon className="w-5 h-5" style={{ color }} />
                      </div>
                      <div>
                        <Title as="h4">{event.event_name}</Title>
                        {event.event_type && (
                          <div className="mt-1">
                            <Tag appearance="neutral">
                              {event.event_type}
                            </Tag>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Tag appearance={isPositiveLift ? 'success' : 'danger'}>
                        {isPositiveLift ? (
                          <ArrowupIcon className="w-3 h-3" />
                        ) : (
                          <ArrowDownIcon className="w-3 h-3" />
                        )}
                        {rateLift > 0 ? '+' : ''}{rateLift.toFixed(1)}% lift
                      </Tag>
                      {event.is_significant === true && (
                        <Tag appearance="success">
                          <CheckCircleIcon className="w-3 h-3" />
                          p={event.p_value?.toFixed(3)}
                        </Tag>
                      )}
                      {event.is_significant === false && event.p_value != null && (
                        <Tag appearance="neutral">
                          No sig.
                        </Tag>
                      )}
                    </div>
                  </div>

                  {/* Metrics comparison */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Control */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Text>Control</Text>
                        <Text fontWeight="bold" color="neutral-textHigh" as="span">
                          {event.control.count.toLocaleString()} eventos
                        </Text>
                      </div>
                      <ProgressBar value={controlRate} appearance="primary" />
                      <div className="flex items-center justify-between">
                        <Text fontSize="caption" as="span">{event.control.unique_sessions.toLocaleString()} sesiones</Text>
                        <Text fontSize="caption" as="span">{controlRate.toFixed(2)}% tasa</Text>
                      </div>
                    </div>

                    {/* Treatment */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Text>Tratamiento</Text>
                        <Text fontWeight="bold" color="neutral-textHigh" as="span">
                          {event.treatment.count.toLocaleString()} eventos
                        </Text>
                      </div>
                      <ProgressBar value={treatmentRate} appearance="success" />
                      <div className="flex items-center justify-between">
                        <Text fontSize="caption" as="span">{event.treatment.unique_sessions.toLocaleString()} sesiones</Text>
                        <Text fontSize="caption" as="span">{treatmentRate.toFixed(2)}% tasa</Text>
                      </div>
                    </div>
                  </div>

                  {/* Additional insights */}
                  <div className="mt-3 pt-3 border-t grid grid-cols-3 gap-3">
                    <div>
                      <Text fontSize="caption">Eventos totales</Text>
                      <Text fontWeight="bold" color="neutral-textHigh">
                        {(event.control.count + event.treatment.count).toLocaleString()}
                      </Text>
                    </div>
                    <div>
                      <Text fontSize="caption">Diff. absoluta</Text>
                      <Text fontWeight="bold" color="neutral-textHigh">
                        {(event.treatment.count - event.control.count).toLocaleString()}
                      </Text>
                    </div>
                    <div>
                      <Text fontSize="caption">Promedio/sesión</Text>
                      <Text fontWeight="bold" color="neutral-textHigh">
                        {event.control.unique_sessions > 0
                          ? (event.control.count / event.control.unique_sessions).toFixed(2)
                          : '0.00'}{' '}
                        |{' '}
                        {event.treatment.unique_sessions > 0
                          ? (event.treatment.count / event.treatment.unique_sessions).toFixed(2)
                          : '0.00'}
                      </Text>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </Card.Body>
      </Card>

      {/* Summary insights */}
      <Card>
        <Card.Header>
          <Title as="h3">Insights Clave</Title>
        </Card.Header>
        <Card.Body>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-1">
                Evento Más Frecuente
              </p>
              <Text fontWeight="bold" color="neutral-textHigh">
                {events.reduce((max, event) =>
                  event.control.count + event.treatment.count > max.control.count + max.treatment.count
                    ? event
                    : max
                , events[0])?.event_name || 'N/A'}
              </Text>
            </div>
            <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800">
              <p className="text-sm text-green-700 dark:text-green-300 mb-1">
                Mayor Mejora
              </p>
              <Text fontWeight="bold" color="neutral-textHigh">
                {events.reduce((max, event) => {
                  const lift = calculateLift(
                    event.treatment.unique_sessions / totalSessions.treatment,
                    event.control.unique_sessions / totalSessions.control
                  )
                  return lift > max.lift ? { name: event.event_name, lift } : max
                }, { name: 'N/A', lift: -Infinity }).name}
              </Text>
            </div>
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-700 dark:text-amber-300 mb-1">
                Total Eventos Rastreados
              </p>
              <Text fontWeight="bold" color="neutral-textHigh">
                {events.reduce((sum, event) => sum + event.control.count + event.treatment.count, 0).toLocaleString()}
              </Text>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}
